import { Box } from "@material-ui/core";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core/styles";
import React, { useContext, useEffect, useState } from "react";
import { Provider, useDispatch } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import { enableMapSet } from "immer";

import {
  isCorpTenant,
  PATH_PREMIUM_OFFERS_SHOP,
  useIsAutoApprovalEnabled,
  useIsTravelApp,
  UserSourceProvider,
} from "@capone/common";
import { ExperimentsHookProvider } from "@capone/experiments";
import { fetchUserPolicies } from "b2b-base/src/api/v1/policies/fetchPolicies";
import { useMedalliaFeedback } from "b2b-base/src/components/MedalliaFeedback";
import { AgentBanner } from "halifax";
import { IntlProvider } from "react-intl";
import {
  CallState,
  CorePremiumStaysCheckout,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  PremierCollectionClientAssetProps,
  PremierCollectionModuleProps,
  SessionInfo,
} from "redmond";
import { config } from "./api/config";
import fetchUserInfo from "./api/v0/user/fetchUserInfo";
import AxiosInterceptors from "./components/AxiosInterceptors";
import { PortalComponentProvider } from "./components/PortalComponentProvider";
import ExperimentsProvider, {
  getExperimentVariant,
  getExperimentVariantJSON,
  HBUX_LODGING_ERROR_COPY_UPDATE,
  HOTEL_CFAR_MODEL_V1,
  HOTELS_CFAR,
  useExperiments,
} from "./context/experiments";
import useWindowEventListener from "./hooks/useWindowEventListener";
import * as englishTranslations from "./lang/en.json";
import * as frenchTranslations from "./lang/fr.json";
import { PremierCollectionAncillary } from "./modules/ancillary";
import { setAncillaryExperiments } from "./modules/ancillary/actions/actions";
import { PremierCollectionAvailability } from "./modules/availability";
import { PremierCollectionBook } from "./modules/book";
import { PriceMatchBanner } from "./modules/book/components/PriceMatchBanner";
import {
  fetchRewardsAccounts,
  setAgentEmail,
  setIsAutoApprovalEnabled,
  setIsFirstLaunch,
  setUserInfo,
} from "./modules/rewards/actions/actions";
import { RewardsBanner } from "./modules/rewards/components";
import { PremierCollectionSearch } from "./modules/search";
import { PremierCollectionShop } from "./modules/shop";
import { PremierCollectionShopDetails } from "./modules/shop-details";
import { VacationRentalShop } from "./modules/vacation-rental-shop";
import { VibesLandingPage } from "./modules/vibes";
import { store } from "./store";
import { eventsToListen } from "./utils/events";
import {
  PATH_ANCILLARY,
  PATH_AVAILABILITY,
  PATH_BOOK,
  PATH_HOME,
  PATH_HOME_SEARCH,
  PATH_SHOP,
  PATH_VACATION_RENTALS,
  PATH_VACATION_RENTALS_AVAILABILITY,
  PATH_VACATION_RENTALS_BOOK,
  PATH_VACATION_RENTALS_SEARCH,
  PATH_VACATION_RENTALS_SHOP,
  PATH_VIBES,
} from "./utils/paths";
import { PremiumOfferShop } from "./modules/offers";

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}

const generateClassName = createGenerateClassName({
  productionPrefix: "ptPremierCollectionModule",
  seed: "ptPremierCollectionModule",
});

interface PremierCollectionClientContextType
  extends PremierCollectionClientAssetProps {
  isAgentPortal: boolean;
  isAutoApprovalEnabled: boolean;
  showCoreHomepage?: boolean;
}

export const ClientContext = React.createContext<
  Partial<PremierCollectionClientContextType>
>({});

const App = (
  props: PremierCollectionModuleProps & {
    // Set by LegacyPremierCollectionBook/LegacyPremierCollectionVRBook, which
    // render this App directly inside the host's own React tree (nested under
    // BaseProviders' own CaponePortal/CorporatePortal). The standalone
    // microfrontend mount (PremierCollectionModule in b2b-base) never sets
    // this — it renders into its own isolated React root with no host
    // ancestor providers, so it needs PortalComponentProvider to supply them.
    skipPortalProvider?: boolean;
  },
) => {
  const {
    theme,
    language,
    baseHistory,
    clientAssets,
    isAgentPortal,
    experiments,
    showCoreHomepage,
    core,
    skipPortalProvider,
  } = props;

  const [activeTheme, setActiveTheme] = useState(theme);
  const [locale, setLocale] = useState(language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);
  const [policies, setPolicies] = useState(clientAssets.policies);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfoResponse = await fetchUserInfo();
        setSessionInfo(userInfoResponse);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (!policies && isCorpTenant(config.TENANT)) {
      handleFetchPolicies();
    }
  }, [policies]);

  const handleFetchPolicies = async () => {
    const policiesResponse = await fetchUserPolicies();
    setPolicies(policiesResponse.policyDetail);
  };

  enableMapSet();

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const messages = loadLocaleData(e.detail);
    setMessages(messages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged,
  );
  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  const isAutoApprovalEnabled = useIsAutoApprovalEnabled(
    clientAssets.policies || policies,
  );

  const { isTravelApp } = useIsTravelApp();
  const { isRewardsDisabled } = clientAssets;

  return (
    <Provider store={store}>
      <ExperimentsProvider
        initState={{
          experiments: experiments?.experiments || [],
          trackingProperties: experiments?.trackingProperties,
          callState: CallState.NotCalled,
        }}
      >
        <ExperimentsHookProvider isLoggedIn={Boolean(sessionInfo?.csrfToken)}>
          <UserSourceProvider>
            <PortalComponentProvider skip={skipPortalProvider}>
              <Router history={baseHistory}>
                <AxiosInterceptors isAgentPortal={isAgentPortal} />
                <ClientContext.Provider
                  value={{
                    ...clientAssets,
                    sessionInfo,
                    policies,
                    isAgentPortal,
                    isAutoApprovalEnabled,
                    showCoreHomepage,
                  }}
                >
                  <div className="App">
                    <StylesProvider
                      generateClassName={generateClassName}
                      injectFirst
                    >
                      <ThemeProvider theme={activeTheme}>
                        {messages != null ? (
                          <IntlProvider
                            locale={locale}
                            defaultLocale="en"
                            messages={messages}
                          >
                            <Switch>
                              {isAgentPortal ? (
                                <Route path="*">
                                  <AgentBanner
                                    firstName={firstName}
                                    lastName={lastName}
                                  />
                                </Route>
                              ) : isCorpTenant(config.TENANT) ? (
                                <Route path="*">
                                  <RewardsBanner />
                                </Route>
                              ) : (
                                <>
                                  <Route path={PATH_BOOK}>
                                    <Route path={PATH_BOOK}>
                                      {!isTravelApp && !isRewardsDisabled && (
                                        <PriceMatchBanner />
                                      )}
                                    </Route>
                                  </Route>
                                  <Route path="*">
                                    {!isTravelApp && <RewardsBanner />}
                                  </Route>
                                </>
                              )}
                            </Switch>
                            <Body sessionInfo={sessionInfo} core={core} />
                          </IntlProvider>
                        ) : (
                          <div>Loading</div>
                        )}
                      </ThemeProvider>
                    </StylesProvider>
                  </div>
                </ClientContext.Provider>
              </Router>
            </PortalComponentProvider>
          </UserSourceProvider>
        </ExperimentsHookProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

export const Body = (props: {
  sessionInfo?: SessionInfo;
  core?: CorePremiumStaysCheckout;
}) => {
  const { sessionInfo, core } = props;
  const dispatch = useDispatch();
  const expState = useExperiments();

  useEffect(() => {
    if (sessionInfo) {
      dispatch(setIsFirstLaunch(sessionInfo.isFirstSession));
      dispatch(fetchRewardsAccounts(true, sessionInfo));
      dispatch(setUserInfo(sessionInfo.userInfo));
      if (sessionInfo.isDelegatedSession) {
        dispatch(setAgentEmail(sessionInfo.isDelegatedSession));
      }
    }
  }, [sessionInfo, dispatch]);

  useEffect(() => {
    dispatch(
      setAncillaryExperiments({
        [HOTELS_CFAR]: getExperimentVariant(expState.experiments, HOTELS_CFAR),
        [HOTEL_CFAR_MODEL_V1]: getExperimentVariantJSON(
          expState.experiments,
          HOTEL_CFAR_MODEL_V1,
        ),
        [HBUX_LODGING_ERROR_COPY_UPDATE]: getExperimentVariant(
          expState.experiments,
          HBUX_LODGING_ERROR_COPY_UPDATE,
        ),
      }),
    );
  }, [expState]);

  const { isAutoApprovalEnabled } = useContext(ClientContext);

  useEffect(() => {
    dispatch(setIsAutoApprovalEnabled(Boolean(isAutoApprovalEnabled)));
  }, [dispatch, isAutoApprovalEnabled]);

  useMedalliaFeedback();

  return (
    <Box className="main-section">
      <Route
        path={[
          PATH_HOME,
          PATH_HOME_SEARCH,
          PATH_VACATION_RENTALS,
          PATH_VACATION_RENTALS_SEARCH,
        ]}
        exact
      >
        <PremierCollectionSearch />
      </Route>
      <Route path={[PATH_AVAILABILITY, PATH_VACATION_RENTALS_AVAILABILITY]}>
        <PremierCollectionAvailability />
      </Route>
      <Route path={PATH_SHOP}>
        <PremierCollectionShop />
      </Route>
      <Route path={PATH_PREMIER_COLLECTION_SHOP_DETAILS}>
        <PremierCollectionShopDetails />
      </Route>
      <Route path={PATH_VACATION_RENTALS_SHOP}>
        <VacationRentalShop />
      </Route>
      <Route path={PATH_ANCILLARY}>
        <PremierCollectionAncillary />
      </Route>
      <Route path={PATH_BOOK}>
        <PremierCollectionBook core={core} />
      </Route>
      <Route path={PATH_VACATION_RENTALS_BOOK}>
        <PremierCollectionBook isVacationRentalBooking />
      </Route>
      <Route path={PATH_PREMIUM_OFFERS_SHOP}>
        <PremiumOfferShop />
      </Route>
      <Route path={PATH_VIBES}>
        <VibesLandingPage />
      </Route>
    </Box>
  );
};

export default App;
